import { useState } from "react";
import { Search, Filter, Download, ClipboardList } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuditLogs } from "@/hooks/useAuditLog";

const actionColors: Record<string, string> = {
  "Booking approved": "bg-success/10 text-success",
  "Booking blocked": "bg-destructive/10 text-destructive",
  "Host approved": "bg-info/10 text-info",
  "Host rejected": "bg-destructive/10 text-destructive",
  "Host verified": "bg-info/10 text-info",
  "Fee collected": "bg-success/10 text-success",
  "Registration expiry warning": "bg-warning/10 text-warning",
  "Booking created": "bg-info/10 text-info",
  "Booking pending": "bg-warning/10 text-warning",
  "Config updated": "bg-primary/10 text-primary",
  "Dispute filed": "bg-warning/10 text-warning",
  "Dispute approved": "bg-success/10 text-success",
  "Dispute rejected": "bg-destructive/10 text-destructive",
};

export default function AuditLog() {
  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState("all");
  const { data: logs = [], isLoading, error } = useAuditLogs(actionFilter);

  const filtered = logs.filter((l: any) =>
    (l.action || "").toLowerCase().includes(search.toLowerCase()) ||
    (l.detail || "").toLowerCase().includes(search.toLowerCase()) ||
    (l.entity || "").toLowerCase().includes(search.toLowerCase())
  );

  const handleExport = () => {
    if (filtered.length === 0) return;
    const headers = ["Timestamp", "User", "Action", "Entity", "Detail"];
    const rows = filtered.map((l: any) => [
      new Date(l.created_at).toLocaleString(),
      l.user_name ?? "",
      l.action ?? "",
      l.entity ?? "",
      (l.detail ?? "").replace(/,/g, ";"),
    ]);
    const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (error) {
    return (
      <div className="flex items-center justify-center p-12">
        <p className="text-sm text-destructive">Failed to load audit logs.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Audit Log</h1>
          <p className="text-sm text-muted-foreground">Complete history of all system actions</p>
        </div>
        <Button variant="outline" size="sm" onClick={handleExport} disabled={filtered.length === 0}><Download className="mr-1 h-4 w-4" /> Export CSV</Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Search actions..." className="pl-9 h-9" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <Select value={actionFilter} onValueChange={setActionFilter}>
              <SelectTrigger className="h-9 w-[160px] text-xs"><Filter className="mr-1 h-3 w-3" /><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                <SelectItem value="booking">Bookings</SelectItem>
                <SelectItem value="host">Host Management</SelectItem>
                <SelectItem value="fee">Financial</SelectItem>
                <SelectItem value="config">Configuration</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <ClipboardList className="h-10 w-10 text-muted-foreground/50 mb-3" />
              <p className="text-sm font-medium text-muted-foreground">No audit logs found</p>
              <p className="text-xs text-muted-foreground">
                {search || actionFilter !== "all" ? "Try adjusting your filters." : "No actions have been recorded yet."}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Timestamp</TableHead>
                  <TableHead className="text-xs">User</TableHead>
                  <TableHead className="text-xs">Action</TableHead>
                  <TableHead className="text-xs">Entity</TableHead>
                  <TableHead className="text-xs">Detail</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((log: any) => (
                  <TableRow key={log.id}>
                    <TableCell className="text-xs font-mono text-muted-foreground">
                      {new Date(log.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-xs">{log.user_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-[10px] ${actionColors[log.action] || "bg-muted text-muted-foreground"}`}>
                        {log.action}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs font-medium">{log.entity}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[300px] truncate">{log.detail}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
